/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import {
  Col,
  Row,
  Typography,
  TableColumnsType,
  App,
  Button as ButtonAnt,
  Tooltip
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { Table } from './table';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import {
  deleteRole,
  resetMessages,
  watchRoleMessage
} from '@/redux/slices/roles.slice';

import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import {
  getListRoles,
  watchRoleList,
  watchRoleLoading,
  watchRolePagination
} from '@/redux/slices/roles.slice';
import { useCallback, useEffect, useState } from 'react';
import { PAGINATION_PARAMS } from '@/constants/pagination.constant';
import { Button } from '../button';
import { useQuery } from '@/hooks/query.hook';
import { PermissionMode } from '@/enums/mode.enum';
import { PageURLs } from '@/utils/navigate';
import { setMessages } from '@/redux/slices/app.slice';
import { formatDateToDDMMYYYY } from '@/utils/common';
import usePermission from '@/hooks/permission.hook';

type Filter = {
  textSearch?: string;
};

type PageChangeParams = {
  page?: number;
  pageSize?: number;
  filter?: Filter;
};

export function RolesTable() {
  const { modal } = App.useApp();
  const { push } = useQuery();
  const dispatch = useAppDispatch();
  const rolesList = useAppSelector(watchRoleList);
  const loading = useAppSelector(watchRoleLoading);
  const pagination = useAppSelector(watchRolePagination);
  const messageInfo = useAppSelector(watchRoleMessage);

  const { creatable, updatable, deletable } = usePermission();

  const [filter, setFilter] = useState<Filter>({});

  const confirmDelete = (roleId: number) => {
    modal.confirm({
      title: 'Bạn có chắc chắn xoá vai trò này không?',
      content: (
        <em>
          Khi xóa vai trò này, vai trò tương ứng của quản trị viên cũng sẽ bị
          xóa, và quản trị viên sẽ không thể truy cập các chức năng mà vai trò
          này đã được phân quyền.
        </em>
      ),
      okText: 'Xác nhận',
      width: 450,
      cancelText: 'Hủy',
      onOk: () => handleDetele(roleId)
    });
  };

  const handleDetele = async (roleId: number) => {
    await dispatch(deleteRole(roleId));
    const remainingRecords = rolesList.length - 1;
    if (remainingRecords === 0 && pagination.curPage > 1) {
      handlePageChange({
        page: pagination.curPage - 1,
        pageSize: pagination.limitPage,
        filter
      });
    } else {
      handlePageChange({
        page: pagination.curPage,
        pageSize: pagination.limitPage,
        filter
      });
    }
  };

  const handleEditRole = (roleId: string) => {
    push(
      `${PageURLs.ofRoleManagement()}?mode=${PermissionMode.EDIT}&roleId=${roleId}`
    );
  };

  const handleSearch = (values: Filter) => {
    const newFilter: Filter = {
      ...filter,
      ...values
    };
    setFilter(newFilter);
    handlePageChange({ filter: newFilter });
  };

  const handleAddRole = () => {
    push(`${PageURLs.ofRoleManagement()}?mode=${PermissionMode.CREATE}`);
  };

  const handlePageChange = useCallback(
    ({
      page = PAGINATION_PARAMS.offset,
      pageSize = PAGINATION_PARAMS.limit,
      filter
    }: PageChangeParams) => {
      dispatch(
        getListRoles({
          offset: page,
          limit: pageSize,
          search: filter?.textSearch
        })
      );
    },
    [dispatch]
  );

  const handleReset = () => {
    handlePageChange({ filter });
  };

  useEffect(() => {
    handlePageChange({});
  }, [handlePageChange]);

  useEffect(() => {
    if (messageInfo) {
      dispatch(
        setMessages([
          {
            type: messageInfo.type,
            message: messageInfo.message,
            description: messageInfo.description
          }
        ])
      );
      dispatch(resetMessages());
    }
  }, [dispatch, messageInfo]);

  const columns: TableColumnsType<UserRole> = [
    {
      title: 'Vai trò',
      dataIndex: 'name',
      key: 'name'
    },
    {
      title: 'Mô tả',
      dataIndex: 'desc',
      key: 'desc'
    },
    {
      title: 'Người tạo',
      dataIndex: 'createdBy',
      key: 'createdBy'
    },
    {
      title: 'Ngày tạo',
      dataIndex: 'createdDate',
      key: 'createdDate',
      render: (record) => formatDateToDDMMYYYY(record)
    },
    {
      title: 'Người cập nhật',
      dataIndex: 'modifiedBy',
      key: 'modifiedDate'
    },
    {
      title: 'Ngày cập nhật',
      dataIndex: 'modifiedDate',
      key: 'modifiedDate',
      render: (record) => formatDateToDDMMYYYY(record)
    },
    {
      title: 'Thao tác',
      key: 'action',
      fixed: 'right',
      width: 104,
      render: (record) => (
        <Row gutter={[8, 10]}>
          {updatable && (
            <Col>
              <Tooltip title='Cập nhật'>
                <ButtonAnt
                  onClick={() => handleEditRole(record.id)}
                  type='primary'
                  icon={<EditOutlined />}
                />
              </Tooltip>
            </Col>
          )}
          {deletable && (
            <Col>
              <Tooltip title='Xoá'>
                <ButtonAnt
                  onClick={() => confirmDelete(record.id)}
                  type='primary'
                  danger
                  icon={<DeleteOutlined />}
                />
              </Tooltip>
            </Col>
          )}
        </Row>
      )
    }
  ];

  return (
    <div css={rootStyles}>
      <Row justify='space-between'>
        <Col>
          <Typography.Title level={5}>Quản lý vai trò</Typography.Title>
        </Col>
        {creatable && (
          <Col>
            <Button
              onClick={handleAddRole}
              type='primary'
              icon={<PlusOutlined />}
              iconPosition='start'
            >
              Thêm mới vai trò
            </Button>
          </Col>
        )}
      </Row>
      <Table<UserRole>
        rowKey={(record) => record.id}
        columns={columns}
        dataSource={rolesList}
        placeholder='Nhập từ khoá để tìm kiếm tên vai trò'
        loading={loading}
        totalResult={pagination.totalRows}
        pagination={{
          showQuickJumper: false,
          showSizeChanger: true,
          current: pagination.curPage,
          pageSize: pagination.limitPage,
          total: pagination.totalRows,
          onChange: (page, pageSize) => {
            handlePageChange({ page, pageSize, filter });
          }
        }}
        onFilter={handleSearch}
        onReset={handleReset}
      />
    </div>
  );
}

const rootStyles = css`
  padding: 2rem 2.4rem;
  display: flex;
  flex-direction: column;
  gap: 2.4rem;
`;
